/**
 * IndexedDB-backed app data cache with in-memory layer for fast sync reads.
 * Used for tickets, ticket metadata, and stories list (offline + faster boot).
 */

import { shouldAllowStaleCache } from "./offline";

const DB_NAME = "hami-app-cache";
const STORE_NAME = "entries";
const DB_VERSION = 1;
const MIGRATION_FLAG = "hami_idb_migrated_v1";

export const CACHE_KEYS = {
  ticketsSidebar: "tickets_sidebar",
  ticketsArchive: "tickets_archive",
  ticket: (id) => `ticket:${id}`,
  ticketData: (id) => `ticket_data:${id}`,
  storiesList: "stories_list",
  storiesCanPost: "stories_can_post",
};

export const TTL = {
  ticketsSidebar: 60 * 60 * 1000,
  ticket: 60 * 60 * 1000,
  ticketData: 24 * 60 * 60 * 1000,
  storiesList: 10 * 60 * 1000,
  storiesCanPost: 10 * 60 * 1000,
};

const LEGACY_KEYS = {
  sidebar: "hami_tickets_sidebar",
  ticketPrefix: "hami_ticket_",
  ticketDataPrefix: "hami_ticket_data_",
};

const MAX_TICKET_DATA_ENTRIES = 250;
const MAX_TICKET_ENTRIES = 120;

/** @type {Map<string, { id: string, data: any, cachedAt: number }>} */
const memory = new Map();
let hydratePromise = null;
let migrationDone = false;

function isIndexedDbAvailable() {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("cachedAt", "cachedAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Failed to open IndexedDB"));
  });
}

function runTransaction(mode, handler) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        let settled = false;
        const finishResolve = (value) => {
          if (!settled) {
            settled = true;
            resolve(value);
          }
        };
        const finishReject = (error) => {
          if (!settled) {
            settled = true;
            reject(error);
          }
        };
        tx.oncomplete = () => {
          db.close();
          if (!settled) finishResolve(undefined);
        };
        tx.onabort = () => {
          db.close();
          finishReject(tx.error || new Error("IndexedDB transaction aborted"));
        };
        tx.onerror = () => {
          db.close();
          finishReject(tx.error || new Error("IndexedDB transaction failed"));
        };
        try {
          handler(store, finishResolve, finishReject);
        } catch (error) {
          db.close();
          finishReject(error);
        }
      })
  );
}

function isEntryFresh(entry, ttlMs, allowStale) {
  if (!entry) return false;
  if (ttlMs == null) return true;
  const age = Date.now() - (entry.cachedAt || 0);
  const stale = age > ttlMs;
  if (stale && !allowStale) return false;
  return true;
}

function readLegacySidebar() {
  try {
    const raw = localStorage.getItem(LEGACY_KEYS.sidebar);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.tickets || !Array.isArray(parsed.tickets)) return null;
    return {
      id: CACHE_KEYS.ticketsSidebar,
      data: parsed,
      cachedAt: parsed.timestamp || Date.now(),
    };
  } catch {
    return null;
  }
}

function legacyHasAnyTicketCaches() {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(LEGACY_KEYS.ticketDataPrefix)) return true;
      if (k.startsWith(LEGACY_KEYS.ticketPrefix) && !k.startsWith(LEGACY_KEYS.ticketDataPrefix)) {
        return true;
      }
    }
  } catch {
    // ignore
  }
  return false;
}

async function idbPut(entry) {
  if (!isIndexedDbAvailable()) return false;
  try {
    await runTransaction("readwrite", (store, resolve, reject) => {
      const req = store.put(entry);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error("Failed to write cache entry"));
    });
    return true;
  } catch {
    return false;
  }
}

async function idbGet(id) {
  if (!isIndexedDbAvailable()) return null;
  try {
    return await runTransaction("readonly", (store, resolve, reject) => {
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error || new Error("Failed to read cache entry"));
    });
  } catch {
    return null;
  }
}

async function idbDelete(id) {
  if (!isIndexedDbAvailable()) return;
  try {
    await runTransaction("readwrite", (store, resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error("Failed to delete cache entry"));
    });
  } catch {
    // ignore
  }
}

async function idbGetAll() {
  if (!isIndexedDbAvailable()) return [];
  try {
    return await runTransaction("readonly", (store, resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error || new Error("Failed to read cache entries"));
    });
  } catch {
    return [];
  }
}

async function migrateLegacyLocalStorage() {
  if (migrationDone || typeof localStorage === "undefined") return;
  if (localStorage.getItem(MIGRATION_FLAG) === "1") {
    migrationDone = true;
    return;
  }

  const sidebar = readLegacySidebar();
  if (sidebar) {
    memory.set(sidebar.id, sidebar);
    await idbPut(sidebar);
  }

  const keysToRemove = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;

      if (k.startsWith(LEGACY_KEYS.ticketDataPrefix)) {
        const ticketId = k.slice(LEGACY_KEYS.ticketDataPrefix.length);
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const entry = {
            id: CACHE_KEYS.ticketData(ticketId),
            data: parsed,
            cachedAt: parsed?.timestamp || Date.now(),
          };
          memory.set(entry.id, entry);
          await idbPut(entry);
          keysToRemove.push(k);
        } catch {
          // ignore
        }
        continue;
      }

      if (k.startsWith(LEGACY_KEYS.ticketPrefix)) {
        const ticketId = k.slice(LEGACY_KEYS.ticketPrefix.length);
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const entry = {
            id: CACHE_KEYS.ticket(ticketId),
            data: parsed,
            cachedAt: parsed?.timestamp || Date.now(),
          };
          memory.set(entry.id, entry);
          await idbPut(entry);
          keysToRemove.push(k);
        } catch {
          // ignore
        }
      }
    }

    if (sidebar) keysToRemove.push(LEGACY_KEYS.sidebar);
    keysToRemove.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        // ignore
      }
    });
    localStorage.setItem(MIGRATION_FLAG, "1");
  } catch {
    // ignore migration errors; app keeps working from memory/network
  }

  migrationDone = true;
}

export function hydrateAppDataCache() {
  if (!hydratePromise) {
    hydratePromise = (async () => {
      await migrateLegacyLocalStorage();
      const entries = await idbGetAll();
      entries.forEach((entry) => {
        if (entry?.id) memory.set(entry.id, entry);
      });
    })();
  }
  return hydratePromise;
}

export function hasCacheWithPrefix(prefix) {
  if (memory.size > 0) {
    for (const key of memory.keys()) {
      if (key.startsWith(prefix)) return true;
    }
  }
  return legacyHasAnyTicketCaches();
}

/**
 * @returns {{ data: any, stale: boolean } | null}
 */
export function getCacheSync(id, options = {}) {
  const { ttlMs, allowStale = false } = options;
  const effectiveAllowStale = allowStale || shouldAllowStaleCache();

  let entry = memory.get(id);
  if (!entry && id === CACHE_KEYS.ticketsSidebar) {
    entry = readLegacySidebar();
    if (entry) memory.set(entry.id, entry);
  }

  if (!isEntryFresh(entry, ttlMs, effectiveAllowStale)) return null;
  const stale = ttlMs != null && Date.now() - (entry.cachedAt || 0) > ttlMs;
  return { data: entry.data, stale: !!stale, cachedAt: entry.cachedAt || 0 };
}

/**
 * @returns {Promise<{ data: any, stale: boolean } | null>}
 */
export async function getCache(id, options = {}) {
  // Don't block single-key reads on full-store hydration (idbGetAll can be
  // very slow with large caches); a direct idbGet returns the same data.
  const sync = getCacheSync(id, options);
  if (sync) return sync;

  const entry = await idbGet(id);
  if (entry?.id) memory.set(entry.id, entry);

  const { ttlMs, allowStale = false } = options;
  const effectiveAllowStale = allowStale || shouldAllowStaleCache();
  if (!isEntryFresh(entry, ttlMs, effectiveAllowStale)) return null;
  const stale = ttlMs != null && Date.now() - (entry?.cachedAt || 0) > ttlMs;
  return entry ? { data: entry.data, stale: !!stale, cachedAt: entry.cachedAt || 0 } : null;
}

export async function setCache(id, data, options = {}) {
  const entry = {
    id,
    data,
    cachedAt: Date.now(),
  };
  memory.set(id, entry);
  await idbPut(entry);

  // Eviction needs the hydrated memory index; run it in the background so
  // writes (and anything awaiting them) never wait on it.
  void hydrateAppDataCache().then(() => {
    if (id.startsWith("ticket_data:")) {
      return evictOldestByPrefix("ticket_data:", MAX_TICKET_DATA_ENTRIES);
    }
    if (id.startsWith("ticket:")) {
      return evictOldestByPrefix("ticket:", MAX_TICKET_ENTRIES);
    }
    return undefined;
  });
}

async function evictOldestByPrefix(prefix, maxCount) {
  const entries = [];
  for (const [key, entry] of memory.entries()) {
    if (key.startsWith(prefix)) entries.push(entry);
  }
  if (entries.length <= maxCount) return;
  entries.sort((a, b) => (a.cachedAt || 0) - (b.cachedAt || 0));
  const excess = entries.length - maxCount;
  for (let i = 0; i < excess; i += 1) {
    const id = entries[i].id;
    memory.delete(id);
    await idbDelete(id);
  }
}

export async function deleteCache(id) {
  memory.delete(id);
  await idbDelete(id);
}
